// Batch "Report Forms" — prints every learner's term report card for a class (or
// whole stage) in one job, with a little analysis (position, class comparison,
// best/weakest subject, deviation) plus a class-analysis summary page. Available
// to academic staff and class teachers.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, MenuItem, TextField, Button, Alert,
  IconButton, Tooltip, CircularProgress, Table, TableHead, TableBody, TableRow,
  TableCell, Divider, Chip,
} from '@mui/material';
import { Home, Print, Description, Search } from '@mui/icons-material';
import axios from 'axios';
import ExamSelect, { useTermExams, examParam } from './ExamSelect';
import CompanyHeader from './CompanyHeader';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const pct = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);

// Grade chip colour — CBC band prefix (EE/ME/AE/BE) or 8-4-4 letter family.
const GC = {
  EE: '#2e7d32', ME: '#1565c0', AE: '#ef6c00', BE: '#c62828',
  A: '#2e7d32', B: '#00897b', C: '#1565c0', D: '#ef6c00', E: '#c62828',
};
const gradeColor = (code) => GC[code] || GC[(code || '').slice(0, 2)] || GC[(code || '')[0]] || '#607d8b';

// A one-line remark from the mean %, so each card carries a little analysis.
const remarkFor = (mean) => {
  if (mean == null) return '';
  if (mean >= 75) return 'Excellent performance — keep it up.';
  if (mean >= 60) return 'Good work; maintain the momentum and aim higher.';
  if (mean >= 50) return 'Fair performance; more consistent effort will lift the grade.';
  if (mean >= 40) return 'Below average; needs focused improvement and support.';
  return 'Requires serious improvement — close guidance and extra practice recommended.';
};

export default function ClassReportForms() {
  const navigate = useNavigate();
  const [years, setYears]     = useState([]);
  const [stages, setStages]   = useState([]);
  const [classes, setClasses] = useState([]);
  const [f, setF] = useState({ academicYear: '', term: '1', stageCode: '', classCode: '', exam: '' });
  const exams = useTermExams(f.academicYear, f.term, f.stageCode);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    axios.get(`${API}/api/academics/years`, { headers: hdrs() })
      .then(r => { setYears(r.data || []); const a = (r.data || []).find(y => y.isActive); if (a) setF(s => ({ ...s, academicYear: a.year })); })
      .catch(() => {});
    axios.get(`${API}/api/stages`, { headers: hdrs() }).then(r => setStages(r.data || [])).catch(() => {});
    const role = (JSON.parse(localStorage.getItem('user') || '{}')).role;
    if (role === 'classTeacher') {
      axios.get(`${API}/api/academics/class-teacher/classes`, { headers: hdrs() })
        .then(r => { const c = (r.data || [])[0]; if (c) setF(s => ({ ...s, stageCode: c.stageCode, classCode: c.streamCode ? c.classCode : '' })); })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!f.stageCode) { setClasses([]); return; }
    const role = (JSON.parse(localStorage.getItem('user') || '{}')).role;
    const url = role === 'classTeacher'
      ? `${API}/api/academics/class-teacher/classes`
      : `${API}/api/academics/classes?stageCode=${f.stageCode}`;
    axios.get(url, { headers: hdrs() })
      .then(r => setClasses((r.data || []).filter(c => c.stageCode === f.stageCode)))
      .catch(() => setClasses([]));
  }, [f.stageCode]);

  const run = useCallback(async () => {
    if (!f.academicYear || !f.term || !f.stageCode) { setError('Select academic year, term and grade/stage'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const params = { academicYear: f.academicYear, term: f.term, stageCode: f.stageCode };
      if (f.classCode) params.classCode = f.classCode;
      const examId = examParam(exams, f.exam);   // none = termly weighted average
      if (examId) params.exam = examId;
      const r = await axios.get(`${API}/api/academics/assessment-scores/class-report-forms`, { headers: hdrs(), params });
      setData(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report forms');
    } finally { setLoading(false); }
  }, [f, exams]);

  const scale = data?.scale || [];

  const GradeChip = ({ code, size = 20 }) => (
    <Box component="span" sx={{
      display: 'inline-block', minWidth: size, px: 0.6, py: 0.1, borderRadius: 0.75,
      bgcolor: gradeColor(code), color: '#fff', fontWeight: 700, fontSize: 11, textAlign: 'center',
    }}>{code || '—'}</Box>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 3, mb: 4, px: { xs: 1, sm: 2 } }}>
      <style>{`
        @page { size: A4 portrait; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          #rf-print, #rf-print * { visibility: visible; }
          body * { visibility: hidden; }
          #rf-print { position: absolute; left: 0; top: 0; width: 100%; }
          .report-card { page-break-after: always; }
          .report-card:last-child { page-break-after: auto; }
        }
      `}</style>

      {/* Controls */}
      <Card elevation={3} className="no-print" sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Description color="primary" />
              <Typography variant="h5" color="primary">Report Forms</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Print all report forms"><span>
                <IconButton onClick={() => window.print()} disabled={!data || !data.learners?.length}><Print /></IconButton>
              </span></Tooltip>
              <Tooltip title="Academics"><IconButton onClick={() => navigate('/academics')}><Home /></IconButton></Tooltip>
            </Box>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            <TextField select size="small" label="Academic Year" sx={{ minWidth: 130 }}
              value={f.academicYear} onChange={e => setF(s => ({ ...s, academicYear: e.target.value }))}>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}{y.isActive ? ' (Active)' : ''}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Term" sx={{ width: 110 }}
              value={f.term} onChange={e => setF(s => ({ ...s, term: e.target.value }))}>
              {[1, 2, 3].map(t => <MenuItem key={t} value={String(t)}>Term {t}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Grade / Stage" sx={{ minWidth: 150 }}
              value={f.stageCode} onChange={e => setF(s => ({ ...s, stageCode: e.target.value, classCode: '' }))}>
              {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.name || s.code}</MenuItem>)}
            </TextField>
            <ExamSelect exams={exams} value={f.exam} onChange={v => setF(s => ({ ...s, exam: v }))} />
            <TextField select size="small" label="Class" sx={{ minWidth: 150 }}
              value={f.classCode} onChange={e => setF(s => ({ ...s, classCode: e.target.value }))} disabled={!f.stageCode}>
              <MenuItem value=""><em>Whole stage</em></MenuItem>
              {classes.map(c => <MenuItem key={c.classCode} value={c.classCode}>{c.className}</MenuItem>)}
            </TextField>
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
              onClick={run} disabled={loading}>{loading ? 'Loading…' : 'Generate'}</Button>
            {data?.learners?.length > 0 && (
              <Button variant="outlined" startIcon={<Print />} onClick={() => window.print()}>
                Print {data.learners.length} form{data.learners.length !== 1 ? 's' : ''}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {data && data.learners?.length === 0 && (
        <Alert severity="info" className="no-print">No scored learners for this selection.</Alert>
      )}

      {/* Printable report forms */}
      {data && data.learners?.length > 0 && (
        <Box id="rf-print">
          {data.learners.map((l) => {
            const vsClass = (l.mean != null && data.classAnalysis?.classMean != null)
              ? (l.mean - data.classAnalysis.classMean) : null;
            return (
              <Box key={l.admNo} className="report-card"
                sx={{ bgcolor: '#fff', p: { xs: 2, sm: 3 }, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <CompanyHeader title="TERM REPORT FORM" subtitle={`${data.academicYear} · Term ${data.term}`} />

                {/* Learner header */}
                <Box display="flex" flexWrap="wrap" justifyContent="space-between" gap={1} mb={1.5}>
                  <Box>
                    <Typography variant="body2"><strong>Name:</strong> {l.name}</Typography>
                    <Typography variant="body2"><strong>Adm No:</strong> {l.admNo} &nbsp;·&nbsp; <strong>Class:</strong> {l.className}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="body2"><strong>Position:</strong> {l.classPos} of {l.classSize} (class)</Typography>
                    <Typography variant="body2"><strong>Stage position:</strong> {l.overallPos}</Typography>
                  </Box>
                </Box>

                {/* Subjects */}
                <Table size="small" sx={{ '& td, & th': { border: '1px solid #ccc', px: 1, py: 0.4, fontSize: 12 } }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Learning Area</strong></TableCell>
                      <TableCell align="center"><strong>Marks</strong></TableCell>
                      <TableCell align="center"><strong>Out Of</strong></TableCell>
                      <TableCell align="center"><strong>%</strong></TableCell>
                      <TableCell align="center"><strong>Grade</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {l.subjects.map(s => (
                      <TableRow key={s.code}>
                        <TableCell>{s.name}</TableCell>
                        <TableCell align="center">{s.raw ?? '—'}</TableCell>
                        <TableCell align="center">{s.max ?? '—'}</TableCell>
                        <TableCell align="center">{s.pct == null ? '—' : pct(s.pct)}</TableCell>
                        <TableCell align="center">{s.pct == null ? '—' : <GradeChip code={s.grade} />}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>MEAN / OVERALL</strong></TableCell>
                      <TableCell align="center" colSpan={2}><strong>{l.sbj} subjects</strong></TableCell>
                      <TableCell align="center"><strong>{pct(l.mean)}</strong></TableCell>
                      <TableCell align="center"><GradeChip code={l.pl} /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {/* Analysis */}
                <Box mt={1.5} p={1.25} sx={{ border: '1px dashed #bbb', borderRadius: 1, bgcolor: '#fafafa' }}>
                  <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>PERFORMANCE ANALYSIS</Typography>
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    <Typography variant="caption">Class mean: <strong>{pct(data.classAnalysis?.classMean)}</strong></Typography>
                    {vsClass != null && (
                      <Typography variant="caption" sx={{ color: vsClass >= 0 ? 'success.main' : 'error.main' }}>
                        {vsClass >= 0 ? 'Above' : 'Below'} class mean by <strong>{Math.abs(vsClass).toFixed(1)}%</strong>
                      </Typography>
                    )}
                    {l.dev != null && (
                      <Typography variant="caption" sx={{ color: l.dev >= 0 ? 'success.main' : 'error.main' }}>
                        {l.dev >= 0 ? 'Improved' : 'Dropped'} <strong>{Math.abs(l.dev).toFixed(1)}%</strong> vs last term
                      </Typography>
                    )}
                    <Typography variant="caption">Mean points: <strong>{l.meanPoints?.toFixed(2)}</strong></Typography>
                  </Box>
                  {(l.bestSubject || l.weakestSubject) && (
                    <Box display="flex" flexWrap="wrap" gap={2} mt={0.5}>
                      {l.bestSubject && <Typography variant="caption">Strongest: <strong>{l.bestSubject.name}</strong> ({pct(l.bestSubject.pct)})</Typography>}
                      {l.weakestSubject && <Typography variant="caption">Needs work: <strong>{l.weakestSubject.name}</strong> ({pct(l.weakestSubject.pct)})</Typography>}
                    </Box>
                  )}
                  <Typography variant="caption" display="block" mt={0.5} fontStyle="italic">{remarkFor(l.mean)}</Typography>
                </Box>

                {/* Signatures */}
                <Box display="flex" justifyContent="space-between" mt={2.5}>
                  <Typography variant="caption">Class Teacher: ____________________</Typography>
                  <Typography variant="caption">Principal: ____________________</Typography>
                  <Typography variant="caption">Date: ____________</Typography>
                </Box>
              </Box>
            );
          })}

          {/* Class analysis summary (final page) */}
          <Box className="report-card" sx={{ bgcolor: '#fff', p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <CompanyHeader title="CLASS ANALYSIS" subtitle={`${data.className} · ${data.academicYear} · Term ${data.term}`} />
            <Box display="flex" gap={2} flexWrap="wrap" mb={1.5}>
              <Chip label={`Learners: ${data.classAnalysis?.size}`} />
              <Chip label={`Class Mean: ${pct(data.classAnalysis?.classMean)}`} color="primary" />
              <Chip label={`Mean Grade: ${data.classAnalysis?.classMeanGrade || '—'}`} />
            </Box>

            <Typography variant="subtitle2" fontWeight={800} mb={0.5}>Subject Means</Typography>
            <Table size="small" sx={{ mb: 2, '& td, & th': { border: '1px solid #ccc', px: 1, py: 0.4, fontSize: 12 } }}>
              <TableHead><TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Subject</strong></TableCell>
                <TableCell align="center"><strong>Entries</strong></TableCell>
                <TableCell align="center"><strong>Mean %</strong></TableCell>
                <TableCell align="center"><strong>Grade</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {(data.classAnalysis?.subjectMeans || []).map(s => (
                  <TableRow key={s.code}>
                    <TableCell>{s.name}</TableCell>
                    <TableCell align="center">{s.entries}</TableCell>
                    <TableCell align="center">{pct(s.mean)}</TableCell>
                    <TableCell align="center"><GradeChip code={s.grade} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="subtitle2" fontWeight={800} mb={0.5}>Grade Distribution</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {scale.map(g => (
                <Chip key={g.code} size="small"
                  label={`${g.code}: ${data.classAnalysis?.gradeDistribution?.[g.code] || 0}`}
                  sx={{ bgcolor: gradeColor(g.code) + '22', color: gradeColor(g.code), fontWeight: 700 }} />
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Grade key: {scale.map(g => `${g.code} ${g.minPercent}–${g.maxPercent}%`).join('  ·  ')}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
